import { ClipboardList, FileCheck2, FileText, LifeBuoy, Mic, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import styles from "./HomeTips.module.css";

interface HomeTipsProps {
    onDocumentTipClicked: () => void;
    onDocumentVoiceTipClicked?: () => void;
    onHandlingTipClicked: () => void;
    onEndReportTipClicked?: () => void;
    onFormFillTipClicked?: () => void;
}

type HomeTip = {
    id: string;
    icon: LucideIcon;
    title: string;
    subtitle: string;
    onClick: () => void;
    onVoiceClick?: () => void;
};

export const HomeTips = ({
    onDocumentTipClicked,
    onDocumentVoiceTipClicked,
    onHandlingTipClicked,
    onEndReportTipClicked,
    onFormFillTipClicked
}: HomeTipsProps) => {
    const { t } = useTranslation();
    const tips: HomeTip[] = [
        {
            id: "document",
            icon: FileText,
            title: t("homeTips.document.title"),
            subtitle: t("homeTips.document.subtitle"),
            onClick: onDocumentTipClicked,
            onVoiceClick: onDocumentVoiceTipClicked
        },
        {
            id: "handling",
            icon: LifeBuoy,
            title: t("homeTips.handling.title"),
            subtitle: t("homeTips.handling.subtitle"),
            onClick: onHandlingTipClicked
        }
    ];

    if (onEndReportTipClicked) {
        tips.push({
            id: "endReport",
            icon: FileCheck2,
            title: t("homeTips.endReport.title"),
            subtitle: t("homeTips.endReport.subtitle"),
            onClick: onEndReportTipClicked
        });
    }

    if (onFormFillTipClicked) {
        tips.push({
            id: "formFill",
            icon: ClipboardList,
            title: t("homeTips.formFill.title"),
            subtitle: t("homeTips.formFill.subtitle"),
            onClick: onFormFillTipClicked
        });
    }

    return (
        <section className={styles.container} aria-label={t("homeTips.label")}>
            <div className={styles.tipsLabel}>{t("homeTips.label")}</div>
            <ul className={styles.tipsNavList}>
                {tips.map(tip => {
                    const Icon = tip.icon;
                    const voiceActionLabel = t("homeTips.document.voiceAction", "Start documenting with voice");

                    return (
                        <li key={tip.id} className={styles.tipItem}>
                            <div className={`${styles.tipCard} ${tip.onVoiceClick ? styles.tipCardWithVoice : ""}`}>
                                <button type="button" className={styles.tipMainAction} onClick={tip.onClick}>
                                    <Icon size={20} className={styles.tipIcon} />
                                    <div className={styles.tipTitle}>{tip.title}</div>
                                    <div className={styles.tipSubtitle}>{tip.subtitle}</div>
                                </button>
                                {tip.onVoiceClick && (
                                    <button
                                        type="button"
                                        className={styles.tipVoiceButton}
                                        onClick={tip.onVoiceClick}
                                        aria-label={voiceActionLabel}
                                        title={voiceActionLabel}
                                    >
                                        <Mic size={18} />
                                    </button>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
};
